import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, TextField, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { ROUTES } from "../../common/constant/routes";
import {
  GiveResult,
  PromotionAction,
  PromotionDetail,
  changePromotionStatus,
  duplicatePromotion,
  getPromotion,
  givePromotion,
} from "../../common/requests/promotions/endpoints";
import { ConfirmDialog, DetailDialog, DetailList, DetailSection, EmptyState, ErrorState, KpiCard, KpiStrip, errorMessage, useIsNarrow, useResource, useToast } from "../../ui";
import StateBadge from "./StateBadge";
import {
  CLAIM_BASE_URL,
  FUNDING_LABEL,
  TRIGGER_LABEL,
  dayLabel,
  howLabel,
  money,
  monthLabel,
  offerLabel,
  splitList,
  summaryLine,
  ttlLabel,
} from "./promoText";

interface Props {
  id: string | null;
  /** Changes after an edit elsewhere, to load it again */
  version?: number;
  onClose: () => void;
  onEdit: (promotion: PromotionDetail) => void;
  /** After any change, so the list reloads; `openId` opens another promotion (a duplicate) */
  onChanged: (openId?: string) => void;
}

const CONFIRM: Record<PromotionAction, { title: string; body: string; label: string; tone: "default" | "danger" }> = {
  publish: { title: "Publish this promotion?", body: "Buyers can use it from its start date.", label: "Publish", tone: "default" },
  pause: { title: "Pause this promotion?", body: "Buyers can't use it until you resume it. Vouchers already in wallets stay there.", label: "Pause", tone: "default" },
  resume: { title: "Resume this promotion?", body: "Buyers can use it again right away.", label: "Resume", tone: "default" },
  end: { title: "End this promotion now?", body: "Nobody can use it after this, and it can't be restarted. Duplicate it to run it again.", label: "End promotion", tone: "danger" },
};

const DONE: Record<PromotionAction, string> = {
  publish: "Promotion published.",
  pause: "Promotion paused.",
  resume: "Promotion resumed.",
  end: "Promotion ended.",
};

/** Uses per month as plain bars (the design's small chart). */
function MonthBars({ months }: { months: PromotionDetail["stats"]["by_month"] }) {
  const max = Math.max(1, ...months.map((m) => m.uses));
  return (
    <Box>
      <Box
        role="img"
        aria-label={`Uses per month: ${months.map((m) => `${m.month} ${m.uses}`).join(", ")}`}
        sx={{ display: "grid", gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))`, gap: { xs: 0.5, sm: 1.25 }, alignItems: "end", height: 150, borderBottom: `1px solid ${C.line}`, pb: 0.75 }}
      >
        {months.map((m) => (
          <Box key={m.month} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 0.5, height: "100%" }}>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 700 }}>
              {m.uses}
            </Typography>
            <Box title={`${m.month}: ${m.uses} uses, ${money(m.cost)}`} sx={{ width: "100%", height: `${Math.max(2, (m.uses / max) * 110)}px`, bgcolor: m.uses ? C.moss : C.line, borderRadius: "4px 4px 0 0" }} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))`, gap: { xs: 0.5, sm: 1.25 }, mt: 0.5 }}>
        {months.map((m, i) => (
          <Typography key={m.month} component="span" sx={{ fontSize: 11, color: C.muted, textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
            {months.length > 12 && i % 2 ? "" : monthLabel(m.month, i === 0)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

/** Paste or upload usernames / emails; the voucher goes into their wallets. */
function GiveDialog({ promotion, open, onClose, onDone }: { promotion: PromotionDetail; open: boolean; onClose: () => void; onDone: () => void }) {
  const { idToken } = useAuth();
  const narrow = useIsNarrow(600);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<GiveResult | null>(null);
  React.useEffect(() => {
    if (open) {
      setText("");
      setError(null);
      setResult(null);
    }
  }, [open]);
  const buyers = splitList(text.replace(/\s+/g, ","));
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const content = await file.text();
    // A CSV keeps the cells that look like a username or an email; a header row is dropped.
    const cells = splitList(content.replace(/["\r]/g, "").replace(/\s+/g, ",")).filter((c) => !/^(username|email|user|buyer)s?$/i.test(c));
    setText(cells.join("\n"));
    e.target.value = "";
  };
  const give = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await givePromotion(promotion.id, buyers, idToken!);
      setResult(res.data);
      onDone();
    } catch (err) {
      setError(errorMessage(err, "Couldn't give the voucher."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { m: narrow ? 2 : 4 } }}>
      <DialogTitle>Give to buyers</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: C.ink2 }}>
          Usernames or emails, one per line or separated by commas. Buyers who already hold it unused are skipped.
          {promotion.wallet_ttl_hours ? ` Each voucher expires ${ttlLabel(promotion.wallet_ttl_hours)} after it's given.` : ""}
        </Typography>
        {result ? (
          <Alert severity={result.given ? "success" : "info"}>
            Gave it to {result.given} buyer{result.given === 1 ? "" : "s"}
            {result.already_had ? `; ${result.already_had} already had it` : ""}.
            {result.not_found.length > 0 && ` Not found: ${result.not_found.join(", ")}.`}
          </Alert>
        ) : (
          <>
            <TextField label="Buyers" value={text} onChange={(e) => setText(e.target.value)} multiline minRows={5} fullWidth disabled={busy} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Button component="label" variant="outlined" size="small" disabled={busy} sx={{ minHeight: 44 }}>
                Upload CSV or text file
                <input hidden type="file" accept=".csv,.txt,text/csv,text/plain" onChange={onFile} aria-label="Upload a list of buyers" />
              </Button>
              <Typography variant="caption" sx={{ color: C.muted }}>
                {buyers.length} buyer{buyers.length === 1 ? "" : "s"} in the list
              </Typography>
            </Box>
          </>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          {result ? "Done" : "Cancel"}
        </Button>
        {!result && (
          <Button variant="contained" onClick={give} disabled={busy || !buyers.length}>
            {busy ? "Giving…" : `Give to ${buyers.length || ""} buyer${buyers.length === 1 ? "" : "s"}`.replace("  ", " ")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** One promotion: numbers, uses per month, settings, and its actions. Legacy
 * Stripe codes are read-only (Duplicate and Copy code only). */
export default function PromotionDetailDialog({ id, version = 0, onClose, onEdit, onChanged }: Props) {
  const { idToken } = useAuth();
  const toast = useToast();
  const detail = useResource<PromotionDetail>(async () => (await getPromotion(id!, idToken!)).data, [id, idToken, version], {
    enabled: !!id && !!idToken,
    errorText: "Couldn't load this promotion.",
  });
  const [confirm, setConfirm] = React.useState<PromotionAction | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [giving, setGiving] = React.useState(false);

  const p = detail.data && detail.data.id === id ? detail.data : null;
  const notFound = detail.status === "error" && /no promotion/i.test(detail.error ?? "");

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast(`${what} copied.`, "success");
    } catch {
      toast(`Couldn't copy the ${what.toLowerCase()}.`, "error");
    }
  };

  const run = async (action: PromotionAction) => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await changePromotionStatus(p!.id, action, idToken!);
      detail.setData({ ...p!, ...res.data });
      setConfirm(null);
      toast(DONE[action], "success");
      onChanged();
      detail.reload();
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't change the promotion."));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const res = await duplicatePromotion(p!.id, idToken!);
      toast("Duplicated as a draft.", "success");
      onChanged(res.data.id);
    } catch (err) {
      toast(errorMessage(err, "Couldn't duplicate it."), "error");
    } finally {
      setBusy(false);
    }
  };

  const s = p?.stats;
  const running = p && (p.state === "live" || p.state === "scheduled");
  const actions = p && (
    <>
      <Button variant="outlined" onClick={duplicate} disabled={busy}>
        Duplicate
      </Button>
      {p.code && (
        <Button variant="outlined" onClick={() => copy(p.code!, "Code")}>
          Copy code
        </Button>
      )}
      {p.distribution === "claim" && p.claim_slug && (
        <Button variant="outlined" onClick={() => copy(`${CLAIM_BASE_URL}${p.claim_slug}`, "Claim link")}>
          Copy claim link
        </Button>
      )}
      {!p.legacy && p.distribution === "given" && running && (
        <Button variant="outlined" onClick={() => setGiving(true)} disabled={busy}>
          Give to buyers
        </Button>
      )}
      {!p.legacy && p.status !== "ended" && p.state !== "ended" && (
        <Button variant="outlined" onClick={() => onEdit(p)} disabled={busy}>
          Edit
        </Button>
      )}
      {!p.legacy && p.status === "active" && p.state !== "ended" && (
        <Button variant="outlined" onClick={() => setConfirm("pause")} disabled={busy}>
          Pause
        </Button>
      )}
      {!p.legacy && p.status === "paused" && p.state !== "ended" && (
        <Button variant="outlined" onClick={() => setConfirm("resume")} disabled={busy}>
          Resume
        </Button>
      )}
      {!p.legacy && (p.status === "active" || p.status === "paused") && (
        <Button variant="outlined" color="error" onClick={() => setConfirm("end")} disabled={busy}>
          End
        </Button>
      )}
      {!p.legacy && p.status === "draft" && (
        <Button variant="contained" onClick={() => setConfirm("publish")} disabled={busy}>
          Publish
        </Button>
      )}
    </>
  );

  return (
    <>
      <DetailDialog
        open={!!id}
        onClose={onClose}
        size="lg"
        title={p ? p.name : notFound ? "Promotion not found" : "Promotion"}
        status={p && <StateBadge state={p.state} legacy={p.legacy} />}
        subtitle={p ? summaryLine(p) : undefined}
        loading={!p && !notFound && detail.status !== "error"}
        actions={actions || undefined}
      >
        {notFound && <EmptyState title="No promotion with this link." body="It may have been a draft that was replaced. Pick one from the list." />}
        {!notFound && detail.status === "error" && !p && <ErrorState message={detail.error ?? "Couldn't load this promotion."} onRetry={detail.reload} />}
        {p && s && (
          <>
            {p.legacy && (
              <Alert severity="info" icon={false} sx={{ bgcolor: C.ground, color: C.ink }}>
                An old Stripe promotion code from before vouchers. Read-only: Duplicate it to run it as a voucher.
              </Alert>
            )}
            {actionError && <Alert severity="error">{actionError}</Alert>}
            <KpiStrip max={5}>
              <KpiCard title="Used" value={String(s.uses)} sub={p.total_uses ? `of ${p.total_uses}` : undefined} />
              <KpiCard title="Cost" value={money(s.cost)} sub={p.budget ? `of ${money(p.budget)} budget` : undefined} />
              <KpiCard title="Orders it was used on" value={money(s.gmv)} sub={`${s.orders} order${s.orders === 1 ? "" : "s"}`} />
              <KpiCard title="Cost per order" value={s.cost_per_order === null ? "–" : money(s.cost_per_order)} />
              <KpiCard title="Bought again later" value={`${s.bought_again} of ${s.buyers}`} help="Buyers with a paid order after the one that used it." />
            </KpiStrip>

            {running && (
              <DetailSection title="Right now">
                <DetailList
                  columns={3}
                  items={[
                    { label: "Budget used", value: p.budget ? `${Math.round((s.budget_used ?? 0) * 100)}% (${money(s.cost)} of ${money(p.budget)})` : "No budget" },
                    { label: "Uses today", value: s.uses_today },
                    p.distribution === "claim"
                      ? { label: "Claimed", value: `${s.claims}${p.max_claims ? ` of ${p.max_claims}` : ""}` }
                      : p.distribution === "given"
                      ? { label: "Given", value: s.given }
                      : { label: "Saved to wallets", value: s.in_wallets },
                    { label: "Unused in wallets", value: s.in_wallets },
                    { label: p.state === "scheduled" ? "Starts" : "Ends", value: p.state === "scheduled" ? dayLabel(p.starts_at!) : p.expires_at ? dayLabel(p.expires_at) : "No end date" },
                  ]}
                />
              </DetailSection>
            )}

            {s.by_month.length > 0 && (
              <DetailSection title="Uses per month">
                <MonthBars months={s.by_month} />
              </DetailSection>
            )}

            <DetailSection title="Settings">
              <DetailList
                items={[
                  { label: "Offer", value: offerLabel(p) },
                  { label: "Applies to", value: p.scope === "all" ? "All plants" : p.scope_labels.join(", ") },
                  {
                    label: "Who",
                    value: [
                      howLabel(p),
                      p.code ? `code ${p.code}` : null,
                      p.distribution === "claim" && p.claim_slug ? `${CLAIM_BASE_URL}${p.claim_slug}` : null,
                      p.give_trigger ? TRIGGER_LABEL[p.give_trigger].toLowerCase() : null,
                      p.first_order_only ? "first orders only" : null,
                    ]
                      .filter(Boolean)
                      .join("; "),
                  },
                  {
                    label: "Limits",
                    value: `${p.uses_per_buyer} per buyer${p.per_buyer_window_days ? ` every ${p.per_buyer_window_days} days` : ""}, ${p.total_uses ? `${p.total_uses} in total` : "no total cap"}${p.budget ? `, budget ${money(p.budget)}` : ""}`,
                  },
                  { label: p.state === "ended" ? "Who paid" : "Who pays", value: FUNDING_LABEL[p.funded_by] },
                  { label: "Buyer title", value: p.title || "The standard wording" },
                ]}
              />
            </DetailSection>

            <DetailSection title="Recent uses">
              {s.recent.length ? (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column" }}>
                  {s.recent.map((r) => (
                    <Box
                      component="li"
                      key={r.order_id}
                      sx={{ display: "flex", gap: 1.5, alignItems: "center", py: 1, borderBottom: `1px solid ${C.line}`, fontSize: 13.5, flexWrap: "wrap" }}
                    >
                      <Link component={RouterLink} to={`${ROUTES.SALES}?q=${encodeURIComponent(r.order_id)}&order=${encodeURIComponent(r.order_id)}`} sx={{ fontWeight: 600, minHeight: 24 }}>
                        Order {r.order_id.slice(0, 8)}
                      </Link>
                      <Box component="span" sx={{ color: C.ink2, flex: 1, minWidth: 120 }}>
                        {r.buyer ?? "–"}
                      </Box>
                      <Box component="span" sx={{ color: C.muted }}>
                        {r.at ? dayLabel(r.at, true) : ""}
                      </Box>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        −{money(r.amount, r.currency)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: C.muted }}>
                  Nobody has used it on a paid order yet.
                </Typography>
              )}
            </DetailSection>
          </>
        )}
      </DetailDialog>
      {confirm && (
        <ConfirmDialog
          open
          title={CONFIRM[confirm].title}
          body={CONFIRM[confirm].body}
          confirmLabel={CONFIRM[confirm].label}
          tone={CONFIRM[confirm].tone}
          busy={busy}
          error={actionError}
          onConfirm={() => run(confirm)}
          onClose={() => {
            setConfirm(null);
            setActionError(null);
          }}
        />
      )}
      {p && <GiveDialog promotion={p} open={giving} onClose={() => setGiving(false)} onDone={detail.reload} />}
    </>
  );
}
